import Button from '@/components/Button';
import Image from '@/components/Image';
import { useStore } from '@/providers/app-store-provider';
import { colors } from '@/theme/zeelool.theme';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { styled } from '@mui/material/styles';
import { CheckOutlined } from '@zeelool/icon';
import { useEffect, useRef, useState } from 'react';
import CouponListM from './CouponList.m';
import { useTranslations } from 'next-intl';

const CouponContainer = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexDirection: 'column',
}));

const LeftSection = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  '& img': {
    width: 20,
    height: 20,
  },
  '& span': {
    marginTop: 2,
    marginLeft: 8,
    fontSize: 14,
  },
  '& .pointAvailable': {
    marginLeft: 0,
    color: colors.auxiliaryLilacPurple,
  },
}));

const RightSection = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  '& span': {
    fontSize: 14,
    fontFamily: 'var(--font-bricolage-semibold)',
    fontWeight: 600,
  },
  '& .discount, & .couponAvailable': {
    color: colors.warning,
    fontSize: 14,
  },
  '& .notAvailable': {
    color: colors.auxiliaryText,
  },
  '& .iconfont': {
    marginLeft: 10,
    fontSize: 10,
  },
}));

interface EntranceMProps {
  price: any;
  canUseLength: number;
  couponCode: string;
  applyFlag: boolean;
  checkCoupon: boolean;
  couponCodeText: string;
  onChangeCouponCode: (code: string) => void;
  applyLoading: boolean;
  handleCodeApply: () => void;
  handleCodeCancel: () => void;
  changeApplyFlag: (flag: boolean) => void;
  usableCouponList: any[];
  handleCouponApply: (item: any) => void;
}

export default function EntranceM(props: EntranceMProps) {
  const {
    price,
    canUseLength,
    couponCode,
    applyFlag,
    checkCoupon,
    couponCodeText,
    onChangeCouponCode,
    applyLoading,
    handleCodeApply,
    handleCodeCancel,
    usableCouponList,
    handleCouponApply,
    changeApplyFlag,
  } = props;
  const t = useTranslations('coupons');
  const codeDiscount = price?.code_discount ? Number(price?.code_discount) : null;

  const currency = useStore((state) => state.currency);

  const [show, setShow] = useState(false);
  const couponListRef = useRef<any>(null);
  useEffect(() => {
    if (applyFlag) {
      setShow(false);
    }
  }, [applyFlag]);

  useEffect(() => {
    if (show) {
      changeApplyFlag(false);
    }
  }, [show]);

  return (
    <CouponContainer>
      <div className="flex justify-between items-center w-full mb-18">
        <LeftSection>
          <Image src="/payment/code.png" alt="zeelool glasses" width={20} height={20} />
          <h3 className="text-[14px] ml-8 font-br">
            {t('Code')} / {t('Coupon')}
          </h3>
        </LeftSection>
        <RightSection>
          {codeDiscount !== null && codeDiscount >= 0 && (
            <span className="discount notranslate">
              -{formatPriceNumberWithSymbol(price.code_discount || '', currency)}
            </span>
          )}
          {codeDiscount !== null && codeDiscount < 0 && (
            <span className="discount notranslate">
              +{formatPriceNumberWithSymbol(String(Math.abs(codeDiscount)), currency)}
            </span>
          )}
          {codeDiscount === null && canUseLength > 0 && (
            <span className="couponAvailable">
              ({canUseLength > 1 ? t('availableCoupons') : t('availableCoupon')}: {canUseLength})
            </span>
          )}
          {codeDiscount === null && canUseLength === 0 && <span>{t('UseCouponCode')}</span>}
        </RightSection>
      </div>

      <div className="flex justify-between items-center w-full relative">
        <div
          className="flex justify-between items-center w-full relative"
          onClick={() => {
            if (checkCoupon) {
              // handleCodeCancel();
              // couponListRef.current?.setIsSelectedId(null);
            } else {
              setShow(true);
            }
          }}
        >
          <div className="h-[40px] bg-[#F5F5F5] text-[14px] font-bb text-secondary-text leading-[40px] rounded-[20px] text-left w-full pl-[10px]">
            {checkCoupon ? (
              <div className="flex items-center">
                <CheckOutlined className="text-[#85C05D] mr-4 text-[24px]" />
                {`${couponCodeText ? t('Coupon') : t('Code')} ${t('appliedSuccessfully')}`}
              </div>
            ) : (
              t('enterACouponCode')
            )}
            <span className="icon-success text-[#85C05D]" />
          </div>
          <div className="absolute right-0">
            <Button
              variant="contained"
              color="black"
              sx={{
                height: '40px',
                borderRadius: '28px',
                width: '90px',
              }}
              loading={applyLoading}
              onClick={() => {
                if (checkCoupon) {
                  handleCodeCancel();
                  couponListRef.current?.setIsSelectedId(null);
                } else {
                  setShow(true);
                }
              }}
            >
              {checkCoupon ? t('Cancel') : t('Apply')}
            </Button>
          </div>
        </div>
      </div>

      <CouponListM
        show={show}
        onClose={() => setShow(false)}
        couponCode={couponCode}
        onChangeCouponCode={onChangeCouponCode}
        checkCoupon={checkCoupon}
        applyLoading={applyLoading}
        handleCodeApply={handleCodeApply}
        usableCouponList={usableCouponList}
        handleCouponApply={handleCouponApply}
        ref={couponListRef}
      />
    </CouponContainer>
  );
}
